export const HELPLY_SRC = 'https://cdn.helply.com/helply-agent.js';
const HELPLY_HOST_ID = 'helply-agent-root-container';

const MOBILE_QUERY = '(max-width: 768px)';
const DEFAULT_WIDGET_TIMEOUT_MS = 8000;
const DEFAULT_POLL_INTERVAL_MS = 50;

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    helplyAgentWidget?: any;
    HELPLY_AGENT_ID?: any;
    HELPLY_SETTINGS?: Record<string, unknown>;
  }
}

type HelplyWidget = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
};

let loadPromise: Promise<HelplyWidget> | null = null;
let visibilitySyncInstalled = false;
let loadGeneration = 0;

function clearExistingHelplyBootstraps(): void {
  loadGeneration++;

  document
    .querySelectorAll<HTMLScriptElement>(`script[src="${HELPLY_SRC}"]`)
    .forEach((scriptTag) => scriptTag.remove());

  const hostEl = document.getElementById(HELPLY_HOST_ID);
  hostEl?.remove();

  delete window.helplyAgentWidget;
  loadPromise = null;
}

function isMobileViewport(): boolean {
  return (
    (window.matchMedia?.(MOBILE_QUERY)?.matches ?? false) ||
    window.innerWidth <= 768
  );
}

function waitForWidget(
  expectedGeneration: number,
  timeoutMs: number = DEFAULT_WIDGET_TIMEOUT_MS,
  intervalMs: number = DEFAULT_POLL_INTERVAL_MS
): Promise<HelplyWidget> {
  return new Promise((resolve, reject) => {
    const start = Date.now();

    const tick = () => {
      if (loadGeneration !== expectedGeneration) {
        return reject(new Error('Agent changed during widget initialisation'));
      }

      const widget = window.helplyAgentWidget;
      if (widget) return resolve(widget);

      if (Date.now() - start >= timeoutMs) {
        return reject(new Error("Helply widget didn't initialise in time"));
      }

      window.setTimeout(tick, intervalMs);
    };

    tick();
  });
}

function ensureHelplyLoaded(): Promise<HelplyWidget> {
  const agentId = window.HELPLY_AGENT_ID;
  if (!agentId) {
    return Promise.reject(new Error('HELPLY_AGENT_ID is missing'));
  }

  const existingScripts = document.querySelectorAll<HTMLScriptElement>(
    `script[src="${HELPLY_SRC}"]`
  );
  const hasMismatchedAgent = Array.from(existingScripts).some(
    (scriptTag) => scriptTag.getAttribute('data-agent') !== agentId
  );

  if (hasMismatchedAgent) {
    // Clear out any existing Helply bootstrap for a mismatched agent.
    // Do not reset visibilitySyncInstalled here to avoid reinstalling
    // visibility sync and accumulating duplicate observers/listeners.
    clearExistingHelplyBootstraps();
  }

  if (
    window.helplyAgentWidget &&
    !hasMismatchedAgent &&
    existingScripts.length > 0
  ) {
    return Promise.resolve(window.helplyAgentWidget);
  }
  if (loadPromise) return loadPromise;

  const capturedGeneration = loadGeneration;
  loadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${HELPLY_SRC}"]`);
    if (existing) {
      waitForWidget(capturedGeneration).then(resolve).catch(reject);
      return;
    }

    const s = document.createElement('script');
    s.async = true;
    s.setAttribute('data-agent', agentId);
    if (window.HELPLY_SETTINGS) {
      s.setAttribute('data-settings', JSON.stringify(window.HELPLY_SETTINGS));
    }
    s.src = HELPLY_SRC;

    s.onload = () => {
      waitForWidget(capturedGeneration).then(resolve).catch(reject);
    };

    s.onerror = () => reject(new Error('Error on loading Helply script'));

    document.head.appendChild(s);
  });

  return loadPromise;
}

function getHelplyTriggerEl(): HTMLElement | null {
  const host = document.getElementById(HELPLY_HOST_ID) as HTMLElement | null;

  const root = host?.shadowRoot;
  if (!root) return null;

  return (
    (root.querySelector(
      '[part="helply-agent-trigger"]'
    ) as HTMLElement | null) ||
    (root.querySelector(
      'button[aria-label="AI agent trigger"]'
    ) as HTMLElement | null)
  );
}

function syncTriggerVisibilityForViewport(): void {
  const trigger = getHelplyTriggerEl();
  if (!trigger) return;

  if (isMobileViewport()) {
    trigger.style.setProperty('display', 'none', 'important');
  } else {
    trigger.style.removeProperty('display');
  }
}

function installVisibilitySync(): void {
  if (visibilitySyncInstalled) return;
  visibilitySyncInstalled = true;

  const apply = () => syncTriggerVisibilityForViewport();

  const tryAttachShadowObserver = (): boolean => {
    const host = document.getElementById(HELPLY_HOST_ID) as HTMLElement | null;
    const root = host?.shadowRoot;
    if (!root) return false;

    const shadowObserver = new MutationObserver(apply);
    shadowObserver.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    apply();
    return true;
  };

  if (!tryAttachShadowObserver()) {
    const docObserver = new MutationObserver(() => {
      if (tryAttachShadowObserver()) docObserver.disconnect();
    });
    docObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  const mq = window.matchMedia?.(MOBILE_QUERY);
  if (mq?.addEventListener) {
    mq.addEventListener('change', apply);
  } else if (mq?.addListener) {
    mq.addListener(apply);
  } else {
    window.addEventListener('resize', apply);
  }
}

export async function toggleHelplyFromReact(): Promise<void> {
  const widget = await ensureHelplyLoaded();

  installVisibilitySync();
  syncTriggerVisibilityForViewport();

  if (widget.isOpen) {
    widget.close();
  } else {
    widget.open();
  }

  syncTriggerVisibilityForViewport();
}
